"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.requestAssistantOrganizationCredits = exports.requestAssistantCreditCheckout = exports.requestAssistantCreditQuote = exports.requestAssistantCredits = void 0;
const graphql_tag_1 = __importDefault(require("graphql-tag"));
const apollo_1 = require("@app/util/apollo");
const error_handler_1 = require("@app/util/error-handler");
const ASSISTANT_CREDITS_QUERY = (0, graphql_tag_1.default) `
  query AssistantCreditsQuery($workspace: ID!, $sessionId: String) {
    assistantCredits(workspace: $workspace, sessionId: $sessionId) {
      conversationsRemaining
      conversationsGranted
      nearestExpiry
      blocked
      blockedReason
      sessionCharged
    }
  }
`;
/**
 * The workspace's credit balance, expressed in whole conversations.
 *
 * Replaces the deprecated `assistantUsage` facade, whose `resetsAt` no longer describes
 * anything real: credits do not come back at the start of a month, they lapse per package and
 * a top-up raises the ceiling mid-month.
 *
 * Read-only on purpose. The charge itself (`assistantConsumeConversationCredit`) is made by the
 * n8n workflow before it runs the agent: the webhook is public, so a charge made here could be
 * skipped by any client that talks to the webhook directly.
 */
const requestAssistantCredits = async (variables, errorsCallback) => {
    const { data, errors } = await (0, apollo_1.query)(ASSISTANT_CREDITS_QUERY, { variables });
    (0, error_handler_1.processErrors)(errors);
    errorsCallback?.(errors);
    return data?.assistantCredits ?? undefined;
};
exports.requestAssistantCredits = requestAssistantCredits;
const ASSISTANT_CREDIT_QUOTE_QUERY = (0, graphql_tag_1.default) `
  query AssistantCreditQuoteQuery($workspace: ID!, $amount: Int!) {
    assistantCreditQuote(workspace: $workspace, amount: $amount) {
      currency
      amount
      credits
      conversations
      bonusPercent
      minimumAmount
    }
  }
`;
/**
 * What an amount buys, priced by the server.
 *
 * Asked rather than computed here on purpose: the rate and the volume bonus live in one place,
 * the same function the Stripe webhook uses on the amount actually paid — so the preview and the
 * delivered credits cannot drift apart.
 */
const requestAssistantCreditQuote = async (variables, errorsCallback) => {
    const { data, errors } = await (0, apollo_1.query)(ASSISTANT_CREDIT_QUOTE_QUERY, { variables });
    (0, error_handler_1.processErrors)(errors);
    errorsCallback?.(errors);
    return data?.assistantCreditQuote ?? undefined;
};
exports.requestAssistantCreditQuote = requestAssistantCreditQuote;
const ASSISTANT_CREDIT_CHECKOUT_MUTATION = (0, graphql_tag_1.default) `
  mutation AssistantCreditCheckoutMutation(
    $workspace: ID!
    $amount: Int!
    $returnUrl: String
  ) {
    assistantCreditCheckout(
      workspace: $workspace
      amount: $amount
      returnUrl: $returnUrl
    ) {
      url
    }
  }
`;
/**
 * Opens a Stripe Checkout session for a top-up.
 *
 * Protected on the ORGANIZATION rather than the workspace: this spends the organization's money,
 * whereas reading the balance and the price list above does not.
 */
const requestAssistantCreditCheckout = async (variables, errorsCallback) => {
    const { data, errors } = await (0, apollo_1.mutate)(ASSISTANT_CREDIT_CHECKOUT_MUTATION, { variables });
    (0, error_handler_1.processErrors)(errors);
    errorsCallback?.(errors);
    return data?.assistantCreditCheckout ?? undefined;
};
exports.requestAssistantCreditCheckout = requestAssistantCreditCheckout;
const ASSISTANT_ORGANIZATION_CREDITS_QUERY = (0, graphql_tag_1.default) `
  query AssistantOrganizationCreditsQuery($organization: ID!) {
    assistantOrganizationCredits(organization: $organization) {
      workspaceId
      workspaceName
      credits {
        credits
        conversationsRemaining
        conversationsGranted
        nearestExpiry
        packages {
          source
          priority
          amount
          remaining
          conversationsRemaining
          expiresAt
          grantedAt
        }
      }
    }
  }
`;
/**
 * Credit balances of every workspace in the organization, each with its active packages.
 *
 * Kept per workspace on purpose: credits belong to a workspace and cannot be lent between them,
 * so a summed organization total would promise conversations no single workspace can spend.
 * Needs ORGANIZATION_WRITE on the server, same as the top-up.
 */
const requestAssistantOrganizationCredits = async (variables) => {
    const { data, errors } = await (0, apollo_1.query)(ASSISTANT_ORGANIZATION_CREDITS_QUERY, { variables });
    (0, error_handler_1.processErrors)(errors);
    return data?.assistantOrganizationCredits ?? undefined;
};
exports.requestAssistantOrganizationCredits = requestAssistantOrganizationCredits;
exports.default = exports.requestAssistantCredits;
